import { badge } from './ui.js';

// ---------------------------------------------------------------------------
// Status → pill, defined once so a status reads the same on every screen.
// ---------------------------------------------------------------------------

/**
 * One headline status for an organisation, most severe first. Independent
 * flags an operator must still see (e.g. the Trial plan on an inactive org)
 * are shown by the detail page; lists need one answer per row.
 */
export function orgStatus(org) {
  if (org.deletedAt) return { label: 'Deleted', tone: 'red', title: 'Soft-deleted. Its data is retained and it can be restored.' };
  if (!org.isActive) return { label: 'Inactive', tone: 'gray', title: 'Deactivated — every member is blocked on their next request.' };
  // Independent of isActive: an expired trial locks everyone out even while
  // the status toggle still reads Active. Extending the trial end date fixes it.
  if (org.trialExpired) return { label: 'Trial expired', tone: 'red', title: 'The trial end date has passed on the Trial plan, so every member is locked out until it is extended.' };
  // Independent of isActive, same reasoning as trialExpired above: a CMAI
  // Core org whose 12-month access window has passed is locked out even
  // while the status toggle still reads Active. Switching plan again (or
  // extending the window) fixes it.
  if (org.planExpired) return { label: 'Plan expired', tone: 'red', title: 'The CMAI Core access window has ended, so every member is locked out until the organisation is renewed.' };
  // Same reasoning as planExpired: a CMAI Core window an admin scheduled to
  // start later blocks every member until it opens.
  if (org.planNotStarted) return { label: 'Not started', tone: 'amber', title: 'The CMAI Core access window starts in the future, so every member is locked out until the start date.' };
  // Same reasoning: a trial an admin scheduled to start later blocks every
  // member until it opens.
  if (org.trialNotStarted) return { label: 'Not started', tone: 'amber', title: 'The trial starts in the future, so every member is locked out until the trial start date.' };
  if (org.plan === 'TRIAL') return { label: 'Trial', tone: 'amber', title: 'Trial plan: every feature is unlocked. Only the user, project, Coco AI credit and re-validation limits apply, and access ends with the trial window.' };
  return { label: 'Active', tone: 'green', title: 'Active on CMAI Core.' };
}

export function orgStatusBadge(org) {
  const s = orgStatus(org);
  return badge(s.label, s.tone, { title: s.title });
}

const PROJECT_STATUS = {
  DRAFT: ['Draft', 'gray'],
  ACTIVE: ['Active', 'blue'],
  BASELINED: ['Baselined', 'indigo'],
  ARCHIVED: ['Archived', 'gray'],
};

export function projectStatusBadge(status) {
  const [label, tone] = PROJECT_STATUS[status] ?? [status, 'gray'];
  return badge(label, tone);
}

const RUN_STATUS = {
  COMPLETED: ['Completed', 'green'],
  FAILED: ['Failed', 'red'],
  TIMED_OUT: ['Timed out', 'amber'],
  CANCELED: ['Cancelled', 'gray'],
  RUNNING: ['Running', 'blue'],
  PENDING: ['Pending', 'blue'],
  PREPARING: ['Preparing', 'blue'],
};

export function runStatusBadge(status) {
  const [label, tone] = RUN_STATUS[status] ?? [status, 'gray'];
  return badge(label, tone);
}

export const SOURCE_LABEL = {
  GENERATED: 'AI generated',
  IMPORTED_XER: 'XER import',
  IMPORTED_P6: 'P6 import',
  CONTRACTOR_SUBMISSION: 'Contractor submission',
};
